import { NavigationButton } from 'components/eventView/navigation/NavigationRow/NavigationButton';
import { ButtonKeys } from 'components/eventView/pages/eventData';
import { useAnchorScroll } from 'hooks/general/useAnchorScroll';
import { EVPcScrollAnchors } from 'pages/EventViewPage/EventViewPagePC/eVScrollAnchors';
import { useContext, useEffect, useState } from 'react';
import { CtxEventView } from 'session/ctxEventView';
import { Localization } from 'static/localization/localization';

import styled, { css } from 'styled-components/macro';

interface Props {
    disableActions?: boolean;
    className?: string;
    scrollContentRef?: React.RefObject<HTMLDivElement>;
}

export const NavigationRow = (props: Props) => {
    const ctxEventView = useContext(CtxEventView);
    const { eventData, eventPath, isWide } = ctxEventView;
    const { getOffsetTop } = useAnchorScroll();

    const [forcedSelectedIndex, setForcedSelectedIndex] = useState<number | null>(0);

    const order: ButtonKeys[] = !isWide
        ? eventData.eventInfo.order.mobile
        : eventData.eventInfo.order.wide;

    const offsets = () => {
        //take into consideration if the order has all the buttons
        const buttons = order.length;
        const division = buttons + 1;
        const offsets = [0];
        order.map((key) => {
            offsets.push(getOffsetTop(EVPcScrollAnchors[key]) - window.innerHeight / division);
        });
        return offsets;
    };

    useEffect(() => {
        if (!isWide) {
            if (forcedSelectedIndex !== null) {
                setForcedSelectedIndex(null);
            }
            return;
        }

        function handleScroll() {
            const currentOffset = window.scrollY;

            const selectedIndex =
                offsets().length -
                [...offsets()].reverse().findIndex((e) => e <= currentOffset) -
                1;
            if (forcedSelectedIndex !== selectedIndex) {
                setForcedSelectedIndex(selectedIndex);
            }
        }

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [forcedSelectedIndex, isWide, offsets, window.scrollY]);

    const otherEntitiesButtons = ctxEventView.otherEntityDefinitions?.map<
        [string, (forceHighlight: boolean) => React.ReactNode]
    >((e) => [
        e.id,
        (forceHighlight: boolean) => (
            <S_NavigationButton
                key={e.id}
                iconName={e.icon}
                text={e.name}
                linkTo={`/event/${eventPath}/${e.id}`}
                matchChildrenPath
                pcAnchor={e.id}
                forceHighlight={forceHighlight}
                scrollContentRef={props.scrollContentRef}
            />
        )
    ]);

    const buttons: Record<ButtonKeys, (forceHighlight: boolean) => React.ReactNode> = {
        home: (forceHighlight) => (
            <S_NavigationButton
                key="home"
                iconName="home"
                text={Localization.HOME}
                linkTo={`/event/${eventPath}`}
                pcAnchor={null}
                forceHighlight={forceHighlight}
                additionalMatchPaths={[`/event/${eventPath}/highlighted/*`]}
                scrollContentRef={props.scrollContentRef}
            />
        ),
        map: (forceHighlight) => (
            <S_NavigationButton
                key="map"
                iconName="location-dot"
                text={Localization.MAP}
                linkTo={`/event/${eventPath}/map`}
                matchChildrenPath
                pcAnchor={EVPcScrollAnchors.Map}
                forceHighlight={forceHighlight}
                scrollContentRef={props.scrollContentRef}
            />
        ),
        program: (forceHighlight) => (
            <S_NavigationButton
                key="program"
                iconName="clock"
                text={ctxEventView.programTitleShort}
                linkTo={`/event/${eventPath}/program`}
                matchChildrenPath
                pcAnchor={EVPcScrollAnchors.Program}
                forceHighlight={forceHighlight}
                scrollContentRef={props.scrollContentRef}
            />
        ),
        information: (forceHighlight) => (
            <S_NavigationButton
                key="information"
                iconName="circle-info"
                text={Localization.INFO}
                linkTo={`/event/${eventPath}/information`}
                pcAnchor={EVPcScrollAnchors.Information}
                forceHighlight={forceHighlight}
                scrollContentRef={props.scrollContentRef}
            />
        ),
        ...Object.fromEntries(otherEntitiesButtons)
    };

    return (
        <>
            <S_NavigationRow
                className={props.className}
                $isMobile={!isWide}
                $disableActions={props.disableActions}
            >
                {order.map((key, i) => {
                    return buttons[key](forcedSelectedIndex === i);
                })}
            </S_NavigationRow>
        </>
    );
};

const S_NavigationRow = styled.div<{ $isMobile: boolean; $disableActions: boolean }>`
    display: flex;
    align-items: stretch;
    width: 100%;
    text-align: center;
    background: #fff;
    box-shadow: 9.002px 9.002px 45.012px 0px rgba(0, 0, 0, 0.1);

    ${(props) => {
        if (props.$disableActions) {
            return css`
                pointer-events: none;
            `;
        }
    }}

    ${(props) => {
        if (props.$isMobile) {
            return css`
                height: 75px;
                flex-direction: row;
                border-radius: 50px;
            `;
        } else {
            return css`
                flex-direction: column;
                width: 200px;
                border-radius: 15px;
                gap: 1px;
            `;
        }
    }}
`;

const S_NavigationButton = styled(NavigationButton)`
    flex: 1;
    height: 100%;
`;
